import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getDoc, query, where, doc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { calculateRank, skyRoundSlotsFromTeamScores } from '../utils/rankUtils';
import { getDisplayName, getFreshlingDisplayName } from '../utils/flyerDisplay';
import { getActiveEventId } from '../utils/activeEvent';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import './AwardsCeremony.css';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const AWARDS_LEADERBOARD_EXPORT_COLS = [
  { id: 'rank', label: 'Rank', default: true },
  { id: 'division', label: 'Division', default: true },
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'teamName', label: 'Team name', default: true },
  { id: 'total', label: 'Total', default: true },
  { id: 'sky', label: 'Sky', default: true },
  { id: 'bonus', label: 'Bonus', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'mentorEmail', label: 'Mentor email', default: false },
  { id: 'freshlings', label: 'Freshlings', default: true },
  { id: 'skyScores', label: 'Sky rounds', default: false },
];

const LOCAL_STORAGE_KEY_PREFIX = 'fmleaderboard_awards_ceremony_';

const AwardsCeremony = () => {
  const [activeEvent, setActiveEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [scores, setScores] = useState([]);
  const [bonusPuzzles, setBonusPuzzles] = useState([]);
  const [bonusScores, setBonusScores] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customAwards, setCustomAwards] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryTeamId, setNewCategoryTeamId] = useState('');
  const [awardsTableExport, setAwardsTableExport] = useState(null); // { mode } | null
  const [awardsExportCols, setAwardsExportCols] = useState(() => new Set());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Active event
        const id = await getActiveEventId();
        if (!id) {
          setError('No active event found.');
          setLoading(false);
          return;
        }
        const eventSnap = await getDoc(doc(firestore, 'events', id));
        if (!eventSnap.exists()) {
          setError('No active event found.');
          setLoading(false);
          return;
        }
        const event = { id: eventSnap.id, ...eventSnap.data() };
        setActiveEvent(event);

        const [teamSnap, scoreSnap, puzzleSnap, flyerSnap, bonusScoreSnap] =
          await Promise.all([
            getDocs(
              query(
                collection(firestore, 'teams'),
                where('eventId', '==', event.id)
              )
            ),
            getDocs(
              query(
                collection(firestore, 'scores'),
                where('eventId', '==', event.id)
              )
            ),
            getDocs(
              query(
                collection(firestore, 'bonusPuzzles'),
                where('eventId', '==', event.id)
              )
            ),
            getDocs(
              query(
                collection(firestore, 'flyers'),
                where('eventId', '==', event.id)
              )
            ),
            getDocs(
              query(
                collection(firestore, 'bonusScores'),
                where('eventId', '==', event.id)
              )
            ),
          ]);

        const allTeams = teamSnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((team) => !!team.mentor);
        setTeams(allTeams);

        setScores(scoreSnap.docs.map((doc) => doc.data()));

        const puzzles = puzzleSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        puzzles.sort((a, b) => Number(a.number) - Number(b.number));
        setBonusPuzzles(puzzles);

        setFlyers(flyerSnap.docs.map((doc) => doc.data()));

        const bonusData = bonusScoreSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBonusScores(bonusData);
      } catch (err) {
        console.error('Error loading awards ceremony data:', err);
        setError(err.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load custom awards from localStorage once we know the active event.
  useEffect(() => {
    if (!activeEvent) return;
    try {
      const key = `${LOCAL_STORAGE_KEY_PREFIX}${activeEvent.id}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        setCustomAwards([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setCustomAwards(
          parsed.map((item, index) => ({
            id: item.id || `${index}-${item.teamId || ''}`,
            categoryName: item.categoryName || '',
            teamId: item.teamId || '',
          }))
        );
      } else {
        setCustomAwards([]);
      }
    } catch (err) {
      console.error('Error loading custom awards from localStorage:', err);
      setCustomAwards([]);
    }
  }, [activeEvent]);

  const saveCustomAwards = (nextAwards) => {
    setCustomAwards(nextAwards);
    if (!activeEvent) return;
    try {
      const key = `${LOCAL_STORAGE_KEY_PREFIX}${activeEvent.id}`;
      const payload = nextAwards.map(({ id, categoryName, teamId }) => ({
        id,
        categoryName,
        teamId,
      }));
      window.localStorage.setItem(key, JSON.stringify(payload));
    } catch (err) {
      console.error('Error saving custom awards to localStorage:', err);
    }
  };

  const flyersByEmail = useMemo(() => {
    const map = new Map();
    flyers.forEach((f) => {
      if (f && f.email) {
        map.set(String(f.email).toLowerCase().trim(), f);
      }
    });
    return map;
  }, [flyers]);

  const nonTestTeams = useMemo(
    () => teams.filter((t) => !t.test),
    [teams]
  );

  const leaderboardTeams = useMemo(() => {
    if (!nonTestTeams.length) return [];
    const built = nonTestTeams.map((team) => {
      const teamScores = scores.filter((s) => s.teamId === team.id);
      const { numericSkyScores, skyScores } = skyRoundSlotsFromTeamScores(teamScores);
      const skySubtotal = numericSkyScores.reduce((sum, val) => sum + val, 0);
      const teamBonusScores = bonusPuzzles.map((puzzle) => {
        const score = bonusScores.find(
          (b) => b.teamId === team.id && b.puzzleId === puzzle.id
        );
        return score ? score.value : 0;
      });
      const bonusSubtotal = teamBonusScores.reduce((sum, val) => sum + val, 0);
      const total = skySubtotal + bonusSubtotal;
      return {
        id: team.id,
        teamNumber: team.teamNumber || '',
        teamName: team.teamName || '',
        division: team.division || 'Unassigned',
        mentorEmail: team.mentor || '',
        freshlings: team.freshlings || [],
        numericSkyScores,
        skyScores,
        skySubtotal,
        bonusScores: teamBonusScores,
        bonusSubtotal,
        total,
      };
    });
    return calculateRank(built, scores, bonusScores, bonusPuzzles);
  }, [nonTestTeams, scores, bonusPuzzles, bonusScores]);

  const leaderboardById = useMemo(() => {
    const map = new Map();
    leaderboardTeams.forEach((t) => map.set(t.id, t));
    return map;
  }, [leaderboardTeams]);

  const beginnerTop3 = useMemo(() => {
    const list = leaderboardTeams.filter(
      (t) => String(t.division || '').toLowerCase() === 'beginner'
    );
    return [...list].sort((a, b) => a.rank - b.rank).slice(0, 3);
  }, [leaderboardTeams]);

  const intermediateTop3 = useMemo(() => {
    const list = leaderboardTeams.filter(
      (t) => String(t.division || '').toLowerCase() === 'intermediate'
    );
    return [...list].sort((a, b) => a.rank - b.rank).slice(0, 3);
  }, [leaderboardTeams]);

  const topSkyTeam = useMemo(() => {
    if (!leaderboardTeams.length) return null;
    const sorted = [...leaderboardTeams].sort((a, b) => {
      if (b.skySubtotal !== a.skySubtotal) {
        return b.skySubtotal - a.skySubtotal;
      }
      for (let i = 3; i >= 0; i -= 1) {
        const diff = (b.numericSkyScores[i] || 0) - (a.numericSkyScores[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });
    return sorted[0] || null;
  }, [leaderboardTeams]);

  const sub75Freshlings = useMemo(() => {
    const freshlingsList = [];
    leaderboardTeams.forEach((team) => {
      if (!Array.isArray(team.freshlings)) return;
      team.freshlings.forEach((email) => {
        const flyer =
          email && flyersByEmail.get(String(email).toLowerCase().trim());
        if (!flyer) return;
        const jumps = Number(flyer.jumpNumber);
        if (Number.isNaN(jumps) || jumps > 75) return;
        freshlingsList.push({
          email,
          teamId: team.id,
          freshlingName: getFreshlingDisplayName(flyer) || flyer.name || '',
          jumpNumber: jumps,
          totalScore: team.total,
          teamName: team.teamName,
          teamNumber: team.teamNumber,
          teamRank: team.rank,
        });
      });
    });
    freshlingsList.sort((a, b) => a.teamRank - b.teamRank);
    return freshlingsList;
  }, [leaderboardTeams, flyersByEmail]);

  const sub75Top3 = useMemo(
    () => sub75Freshlings.slice(0, 3),
    [sub75Freshlings]
  );

  const getMentorName = (mentorEmail) => {
    if (!mentorEmail) return '';
    const flyer =
      flyersByEmail.get(String(mentorEmail).toLowerCase().trim()) || null;
    if (!flyer) return mentorEmail;
    return getDisplayName(flyer) || flyer.name || mentorEmail;
  };

  const getFreshlingNamesForTeam = (team) => {
    if (!team || !Array.isArray(team.freshlings)) return '';
    const names = team.freshlings
      .map((email) => {
        const flyer =
          email && flyersByEmail.get(String(email).toLowerCase().trim());
        if (!flyer) return null;
        return getFreshlingDisplayName(flyer) || flyer.name || null;
      })
      .filter(Boolean);
    return names.join(', ');
  };

  const handleAddCustomAward = (e) => {
    e.preventDefault();
    const name = (newCategoryName || '').trim();
    const teamId = (newCategoryTeamId || '').trim();
    if (!name || !teamId) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const next = [
      ...customAwards,
      {
        id,
        categoryName: name,
        teamId,
      },
    ];
    saveCustomAwards(next);
    setNewCategoryName('');
    setNewCategoryTeamId('');
  };

  const handleRemoveCustomAward = (id) => {
    const next = customAwards.filter((a) => a.id !== id);
    saveCustomAwards(next);
  };

  const allTeamsForSelect = useMemo(
    () =>
      [...leaderboardTeams].sort((a, b) => {
        const numA = Number(a.teamNumber) || 0;
        const numB = Number(b.teamNumber) || 0;
        if (numA && numB && numA !== numB) return numA - numB;
        return (a.teamName || '').localeCompare(b.teamName || '');
      }),
    [leaderboardTeams]
  );

  /** Plain-text snapshot of on-screen awards (divisions, sky, sub-75, custom categories). */
  const awardsCeremonySummaryText = useMemo(() => {
    if (!activeEvent) return '';
    const mentorLabel = (mentorEmail) => {
      if (!mentorEmail) return '';
      const flyer =
        flyersByEmail.get(String(mentorEmail).toLowerCase().trim()) || null;
      if (!flyer) return mentorEmail;
      return getDisplayName(flyer) || flyer.name || mentorEmail;
    };
    const fmtTeam = (team, extra = '') => {
      if (!team) return '';
      const head = `#${team.teamNumber || '—'} ${team.teamName || '(no name)'}`;
      const score = `Total ${team.total} (Sky ${team.skySubtotal}, Bonus ${team.bonusSubtotal})`;
      return extra ? `${head} — ${score} — ${extra}` : `${head} — ${score}`;
    };
    const lines = [];
    lines.push(activeEvent.title || 'Awards Ceremony');
    if (activeEvent.date) lines.push(String(activeEvent.date));
    lines.push('');

    if (beginnerTop3.length) {
      lines.push('Top 3 Beginner Teams');
      beginnerTop3.forEach((team, i) => {
        lines.push(`  ${i + 1}. ${fmtTeam(team, `Division rank ${team.rank}`)}`);
        lines.push(`     Mentor: ${mentorLabel(team.mentorEmail) || '—'}`);
      });
      lines.push('');
    }

    if (intermediateTop3.length) {
      lines.push('Top 3 Intermediate Teams');
      intermediateTop3.forEach((team, i) => {
        lines.push(`  ${i + 1}. ${fmtTeam(team, `Division rank ${team.rank}`)}`);
        lines.push(`     Mentor: ${mentorLabel(team.mentorEmail) || '—'}`);
      });
      lines.push('');
    }

    if (topSkyTeam) {
      lines.push('Top Sky Score Team (Sky only)');
      lines.push(`  ${fmtTeam(topSkyTeam, `Sky subtotal ${topSkyTeam.skySubtotal}`)}`);
      lines.push(`  Mentor: ${mentorLabel(topSkyTeam.mentorEmail) || '—'}`);
      lines.push('');
    }

    const sub75Places = ['1st', '2nd', '3rd'];
    const anySub75 = sub75Top3.some(Boolean);
    if (anySub75) {
      lines.push('Top 3 Sub-75 Freshlings');
      sub75Top3.forEach((entry, i) => {
        if (!entry) return;
        const team = leaderboardById.get(entry.teamId);
        lines.push(`  ${sub75Places[i]}: ${entry.freshlingName}`);
        lines.push(
          `     Team: #${entry.teamNumber} ${entry.teamName} (team rank ${entry.teamRank})`
        );
        lines.push(`     Total score: ${entry.totalScore}, Jumps: ${entry.jumpNumber}`);
        lines.push(`     Mentor: ${team ? mentorLabel(team.mentorEmail) || '—' : '—'}`);
      });
      lines.push('');
    }

    if (customAwards.length) {
      lines.push('Custom Awards');
      customAwards.forEach((a) => {
        const team = leaderboardById.get(a.teamId);
        lines.push(`  ${a.categoryName || '(category)'}`);
        if (team) {
          lines.push(`     ${fmtTeam(team, `Overall rank ${team.rank}`)}`);
          lines.push(`     Mentor: ${mentorLabel(team.mentorEmail) || '—'}`);
        } else {
          lines.push(`     Team id: ${a.teamId} (not on leaderboard)`);
        }
      });
      lines.push('');
    }

    return lines.join('\n').trimEnd();
  }, [
    activeEvent,
    beginnerTop3,
    intermediateTop3,
    topSkyTeam,
    sub75Top3,
    customAwards,
    leaderboardById,
    flyersByEmail,
  ]);

  const awardsLeaderboardExportRows = useMemo(() => {
    const mentorLabel = (mentorEmail) => {
      if (!mentorEmail) return '';
      const flyer =
        flyersByEmail.get(String(mentorEmail).toLowerCase().trim()) || null;
      if (!flyer) return mentorEmail;
      return getDisplayName(flyer) || flyer.name || mentorEmail;
    };
    const freshlingLabels = (team) => {
      if (!team || !Array.isArray(team.freshlings)) return '';
      const names = team.freshlings
        .map((email) => {
          const flyer =
            email && flyersByEmail.get(String(email).toLowerCase().trim());
          if (!flyer) return null;
          return getFreshlingDisplayName(flyer) || flyer.name || null;
        })
        .filter(Boolean);
      return names.join(', ');
    };
    return [...leaderboardTeams]
      .sort((a, b) => a.rank - b.rank)
      .map((t) => ({
        rank: String(t.rank ?? ''),
        division: t.division || '',
        teamNumber: String(t.teamNumber ?? ''),
        teamName: t.teamName || '',
        total: String(t.total ?? ''),
        sky: String(t.skySubtotal ?? ''),
        bonus: String(t.bonusSubtotal ?? ''),
        mentor: mentorLabel(t.mentorEmail),
        mentorEmail: t.mentorEmail || '',
        freshlings: freshlingLabels(t),
        skyScores: Array.isArray(t.skyScores) ? t.skyScores.join(', ') : '',
      }));
  }, [leaderboardTeams, flyersByEmail]);

  useEffect(() => {
    if (!awardsTableExport) return;
    setAwardsExportCols(
      new Set(AWARDS_LEADERBOARD_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [awardsTableExport]);

  const openAwardsTableExport = (mode) => {
    if (!awardsLeaderboardExportRows.length) {
      window.alert('No leaderboard rows to export.');
      return;
    }
    setAwardsTableExport({ mode });
  };

  const awardsCopyMenuItems = [
    {
      label: 'Copy leaderboard table…',
      disabled: !awardsLeaderboardExportRows.length,
      onSelect: () => openAwardsTableExport('copy'),
      showCopiedFeedback: false,
    },
    {
      label: 'Copy ceremony summary (teams, scores, categories)',
      disabled: !awardsCeremonySummaryText,
      onSelect: () => copyText(awardsCeremonySummaryText),
    },
  ];

  const resetAwardsExportCols = () => {
    setAwardsExportCols(
      new Set(AWARDS_LEADERBOARD_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runAwardsTableExport = (selectedColumnIds) => {
    if (!awardsTableExport) return;
    const { mode } = awardsTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : awardsExportCols;
    const active = AWARDS_LEADERBOARD_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = awardsLeaderboardExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const evTitle = activeEvent?.title || 'Awards';
    const sub = 'Leaderboard';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} team(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setAwardsTableExport(null);
  };

  if (loading) {
    return (
      <div className="awards-ceremony">
        <h2>Awards Ceremony</h2>
        <p>Loading winners…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="awards-ceremony">
        <h2>Awards Ceremony</h2>
        <p className="awards-error">{error}</p>
      </div>
    );
  }

  if (!activeEvent) {
    return (
      <div className="awards-ceremony">
        <h2>Awards Ceremony</h2>
        <p>No active event.</p>
      </div>
    );
  }

  const renderTeamCard = (team, label, extraHeading) => {
    if (!team) return null;
    const mentorName = getMentorName(team.mentorEmail);
    const freshlingNames = getFreshlingNamesForTeam(team);
    return (
      <div className="award-card">
        <div className="award-card-header">
          <div className="award-card-label">{label}</div>
          {extraHeading && (
            <div className="award-card-extra">{extraHeading}</div>
          )}
        </div>
        <div className="award-card-main">
          <div className="award-card-team-name">
            #{team.teamNumber} {team.teamName}
          </div>
          <div className="award-card-score">
            Total: <strong>{team.total}</strong>{' '}
            <span className="award-card-score-breakdown">
              (Sky: {team.skySubtotal}, Bonus: {team.bonusSubtotal})
            </span>
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Freshlings:</span>{' '}
            <span>{freshlingNames || '—'}</span>
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Mentor:</span>{' '}
            <span>{mentorName || '—'}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSub75Card = (entry, placeLabel) => {
    if (!entry) return null;
    const team = leaderboardById.get(entry.teamId);
    if (!team) return null;
    const mentorName = getMentorName(team.mentorEmail);
    const freshlingNames = getFreshlingNamesForTeam(team);
    return (
      <div className="award-card">
        <div className="award-card-header">
          <div className="award-card-label">{placeLabel}</div>
          <div className="award-card-extra">Sub-75 Freshling</div>
        </div>
        <div className="award-card-main">
          <div className="award-card-team-name">
            {entry.freshlingName}
          </div>
          <div className="award-card-score">
            Team: #{entry.teamNumber} {entry.teamName}
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Total Score:</span>{' '}
            <span>{entry.totalScore}</span>
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Jumps:</span>{' '}
            <span>{entry.jumpNumber}</span>
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Team Freshlings:</span>{' '}
            <span>{freshlingNames || '—'}</span>
          </div>
          <div className="award-card-detail">
            <span className="award-card-detail-label">Mentor:</span>{' '}
            <span>{mentorName || '—'}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="awards-ceremony">
      <header className="awards-header">
        <div>
          <h2>{activeEvent.title || 'Awards Ceremony'}</h2>
          {activeEvent.date && (
            <div className="awards-subtitle">{activeEvent.date}</div>
          )}
        </div>
        <div className="awards-header-actions">
          <button
            type="button"
            className="awards-print-button"
            onClick={() => window.print()}
          >
            Print cards
          </button>
          <button
            type="button"
            className="awards-print-button"
            onClick={() => openAwardsTableExport('print')}
            disabled={!awardsLeaderboardExportRows.length}
          >
            Print table
          </button>
          <AdminCopyMenu
            ariaLabel="Copy awards data"
            items={awardsCopyMenuItems}
          />
        </div>
      </header>

      <section className="awards-section">
        <h3>Top 3 Beginner Teams</h3>
        <div className="awards-grid">
          {beginnerTop3.map((team, index) =>
            renderTeamCard(
              team,
              `${index + 1} place – Beginner`,
              `Division rank: ${team.rank}`
            )
          )}
        </div>
      </section>

      <section className="awards-section">
        <h3>Top 3 Intermediate Teams</h3>
        <div className="awards-grid">
          {intermediateTop3.map((team, index) =>
            renderTeamCard(
              team,
              `${index + 1} place – Intermediate`,
              `Division rank: ${team.rank}`
            )
          )}
        </div>
      </section>

      <section className="awards-section">
        <h3>Top Sky Score Team (Sky only)</h3>
        <div className="awards-grid single">
          {renderTeamCard(
            topSkyTeam,
            'Top Sky Score',
            topSkyTeam ? `Sky subtotal: ${topSkyTeam.skySubtotal}` : ''
          )}
        </div>
      </section>

      <section className="awards-section">
        <h3>Top 3 Sub-75 Freshlings</h3>
        <div className="awards-grid">
          {renderSub75Card(sub75Top3[0], '1st place – Sub-75')}
          {renderSub75Card(sub75Top3[1], '2nd place – Sub-75')}
          {renderSub75Card(sub75Top3[2], '3rd place – Sub-75')}
        </div>
      </section>

      <section className="awards-section awards-section-custom">
        <div className="awards-section-header">
          <h3>Custom Awards</h3>
          <p className="awards-section-description">
            Add your own categories like &quot;Best Team Name&quot; or
            &quot;Spirit Award&quot; and pick the team.
          </p>
        </div>

        <form className="awards-custom-form" onSubmit={handleAddCustomAward}>
          <div className="awards-custom-field">
            <label>
              Category name
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Best team name"
              />
            </label>
          </div>
          <div className="awards-custom-field">
            <label>
              Team
              <select
                value={newCategoryTeamId}
                onChange={(e) => setNewCategoryTeamId(e.target.value)}
              >
                <option value="">Select a team…</option>
                {allTeamsForSelect.map((team) => (
                  <option key={team.id} value={team.id}>
                    #{team.teamNumber || '—'} {team.teamName || '(no name)'}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="awards-custom-actions">
            <button
              type="submit"
              disabled={
                !newCategoryName.trim() || !newCategoryTeamId.trim()
              }
            >
              Add category
            </button>
          </div>
        </form>

        {customAwards.length > 0 && (
          <div className="awards-grid">
            {customAwards.map((award) => {
              const team = leaderboardById.get(award.teamId);
              return (
                <div key={award.id} className="awards-custom-card-wrap">
                  {renderTeamCard(
                    team,
                    award.categoryName,
                    team ? `Overall rank: ${team.rank}` : ''
                  )}
                  <button
                    type="button"
                    className="awards-custom-remove"
                    onClick={() => handleRemoveCustomAward(award.id)}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {awardsTableExport && (
        <AdminColumnExportModal
          mode={awardsTableExport.mode}
          columns={AWARDS_LEADERBOARD_EXPORT_COLS}
          selectedIds={awardsExportCols}
          onChangeSelected={setAwardsExportCols}
          onCancel={() => setAwardsTableExport(null)}
          onConfirm={runAwardsTableExport}
          onResetDefaults={resetAwardsExportCols}
        />
      )}
    </div>
  );
};

export default AwardsCeremony;

